import React from 'react';

/**
 * Configuration for a blob group.
 */
export interface BlobConfig {
    id: string;
    color: string;
}

/**
 * Element that contributes to a blob visualization.
 */
export interface BlobElement {
    x: number;
    y: number;
    width: number;
    height: number;
    groupId: string;
}

interface Props {
    elements: BlobElement[];
    groups: BlobConfig[];
    enabled: boolean;
    radiusMultiplier?: number;
    radiusPadding?: number;
}

/**
 * LiquidBlobLayer renders metaball visualizations using SVG filter effects.
 *
 * Uses the "gooey effect" technique:
 * 1. Render circles for each element
 * 2. Apply blur to merge nearby shapes
 * 3. Apply contrast to create sharp edges
 */
export default class LiquidBlobLayer extends React.Component<Props> {
    static defaultProps = {
        radiusMultiplier: 1.5,
        radiusPadding: 50
    };

    private calculateRadius(width: number, height: number): number {
        const { radiusMultiplier = 1.5, radiusPadding = 50 } = this.props;
        return (Math.max(width, height) / 2) * radiusMultiplier + radiusPadding;
    }

    private parseColor(color: string): { opaque: string; opacity: number } {
        const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
        if (match) {
            const [, r, g, b, a] = match;
            return {
                opaque: `rgb(${r}, ${g}, ${b})`,
                opacity: a ? parseFloat(a) : 1
            };
        }
        return { opaque: color, opacity: 1 };
    }

    render(): React.ReactNode {
        if (!this.props.enabled) {
            return null;
        }

        const { elements, groups } = this.props;

        // Performance safety: Limit total elements to prevent browser crashes
        const MAX_ELEMENTS = 100;
        if (elements.length > MAX_ELEMENTS) {
            console.warn(`LiquidBlobLayer: Too many elements (${elements.length}). Maximum is ${MAX_ELEMENTS}. Disabling visualization.`);
            return null;
        }

        // Group elements by groupId
        const elementsByGroup = new Map<string, BlobElement[]>();
        for (const el of elements) {
            if (!elementsByGroup.has(el.groupId)) {
                elementsByGroup.set(el.groupId, []);
            }
            elementsByGroup.get(el.groupId)!.push(el);
        }

        return (
            <g className="liquid-blob-layer">
                <defs>
                    <filter
                        id="gooey-blob-filter"
                        width="300%"
                        x="-100%"
                        height="300%"
                        y="-100%"
                        colorInterpolationFilters="sRGB"
                    >
                        <feGaussianBlur
                            in="SourceGraphic"
                            stdDeviation="30"
                            result="blur"
                        />
                        <feColorMatrix
                            in="blur"
                            mode="matrix"
                            values="1 0 0 0 0
                                    0 1 0 0 0
                                    0 0 1 0 0
                                    0 0 0 18 -9"
                        />
                    </filter>
                </defs>

                {groups.map(group => {
                    const groupElements = elementsByGroup.get(group.id) || [];
                    if (groupElements.length === 0) {
                        return null;
                    }

                    const { opaque, opacity } = this.parseColor(group.color);

                    return (
                        <g
                            key={group.id}
                            className={`liquid-blob liquid-blob-${group.id}`}
                            filter="url(#gooey-blob-filter)"
                            opacity={opacity}
                            style={{
                                pointerEvents: 'none',
                                willChange: 'transform'
                            }}
                        >
                            {groupElements.map((el, index) => (
                                <circle
                                    key={index}
                                    cx={el.x}
                                    cy={el.y}
                                    r={this.calculateRadius(el.width, el.height)}
                                    fill={opaque}
                                />
                            ))}
                        </g>
                    );
                })}
            </g>
        );
    }
}
